import { UserLessonsInfoTable } from "./UserLessonsInfoTable.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { Fragment, PropsWithChildren, useState } from "react";
import { formatDate } from "../UI/Utils/formatDate.ts";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { useCopyToClipboard } from "../UI/Feedback/useCopyToClipboard.ts";
import {
  BusinessEnglishObjective,
  OneOnOneLessonType,
  SpeakingClassLevel,
  User1on1ClassroomPlanGraphqlDto,
  UserFragmentFragment,
} from "../gql/graphql.ts";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardIcon,
  FolderIcon,
  FolderOpenIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline/index.js";
import { InfoFeedback } from "../UI/Feedback/InfoFeedback.tsx";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { Card } from "../UI/Card.tsx";
import { UserForm, UserFormInputs } from "./UserForm.tsx";
import { UserLessonFeedbackComponent } from "./LessonFeedback/UserLessonFeedbackComponent.tsx";
import { UserProgressCheckResultComponent } from "./LessonFeedback/UserProgressCheckResultComponent.tsx";
import { ClassRoomPlanListItem } from "./ClassRoomPlan/ClassRoomPlanListItem.tsx";
import { UserFrozenWeekComponent } from "./ClassRoomPlan/FreezeWeek/UserFrozenWeekComponent.tsx";
import { useUserById } from "./useUserById.ts";
import { useIsActiveUserForAdmin } from "./useIsActiveUserForAdmin.ts";
import { useUpdateUser } from "./useUpdateUser.ts";
import { useUserClassRoomPlans } from "./ClassRoomPlan/useUserClassRoomPlans.ts";
import { useUserRemainingLessons } from "./ClassRoomPlan/useUserRemainingLessons.ts";
import { useDeleteClassRoomPlan } from "./ClassRoomPlan/useDeleteClassRoomPlan.ts";
import { useUser1on1ClassroomPlans } from "./OneOnOne/useUser1on1ClassroomPlans.ts";
import { useUpdateUser1on1ClassroomPlanSpeakingLevel } from "./OneOnOne/useUpdateUser1on1ClassroomPlanSpeakingLevel.ts";
import { useUpdateUser1on1ClassroomPlanLessonType } from "./OneOnOne/useUpdateUser1on1ClassroomPlanLessonType.ts";
import { useGetUserLessonsInfo } from "./useGetUserLessonsInfo.ts";
import { UserClassRoomHistoryComponent } from "./ClassRoomHistory/UserClassRoomHistoryComponent.tsx";
import { Select, SelectOption } from "@normasteaching/norma-ui";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  user: UserFragmentFragment | null;
  onClose: () => void;
  sendResetPasswordEmail?: (user: UserFragmentFragment) => void;
  sendingResetPasswordEmail?: boolean;
  onNavigateToCreateClassRoomPlan: (userId: string) => void;
  onNavigateToEditClassRoomPlan: (userId: string, planId: string) => void;
};

type SectionId =
  | "edit"
  | "progress"
  | "classRoomPlans"
  | "oneOnOnePlans"
  | "lessonsInfo"
  | "classRoomHistory";

// ─── Folder Section ───────────────────────────────────────────────────────────

function FolderSection({
  title,
  isOpen,
  onToggle,
  onNavigateTo,
  children,
}: PropsWithChildren<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  onNavigateTo?: () => void;
}>) {
  return (
    <div className={"border border-gray-200 rounded-lg"}>
      <div className={"flex items-center"}>
        <button
          onClick={onToggle}
          className={
            "flex-1 flex items-center gap-2 text-left p-4 hover:bg-gray-50 transition-colors rounded-lg"
          }
        >
          {isOpen ? (
            <FolderOpenIcon
              className={"w-5 h-5 text-slate-500 flex-shrink-0"}
            />
          ) : (
            <FolderIcon className={"w-5 h-5 text-slate-500 flex-shrink-0"} />
          )}
          <span className={"font-semibold text-base flex-1"}>{title}</span>
          {isOpen ? (
            <ChevronDownIcon className={"w-4 h-4 text-slate-400"} />
          ) : (
            <ChevronRightIcon className={"w-4 h-4 text-slate-400"} />
          )}
        </button>
        {onNavigateTo && (
          <button
            onClick={onNavigateTo}
            title={`Open ${title} page`}
            className={
              "p-4 hover:bg-gray-50 transition-colors text-slate-400 hover:text-slate-600 rounded-lg flex-shrink-0"
            }
          >
            <ArrowTopRightOnSquareIcon className={"w-4 h-4"} />
          </button>
        )}
      </div>
      {isOpen && <div className={"px-4 pb-4"}>{children}</div>}
    </div>
  );
}

// ─── Edit Section ─────────────────────────────────────────────────────────────

function EditSection({
  userId,
  onSaved,
}: {
  userId: string;
  onSaved: () => void;
}) {
  const { user, loading } = useUserById(userId);
  const { isActive, loading: loadingActive } = useIsActiveUserForAdmin(userId);
  const { updateUser } = useUpdateUser();

  if (loading || loadingActive) return <LoadingRows rows={3} />;
  if (!user) return null;

  async function saveUser(data: UserFormInputs): Promise<void> {
    await updateUser({
      speakingLevel: data.speakingLevel,
      userId,
      givenName: data.givenName,
      familyName: data.familyName,
      activateAccount: data.activateAccount,
      hideBookProgressCheckSection: data.hideBookProgressCheckSection,
      hideBookSpeakingClassSection: data.hideBookSpeakingClassSection,
      userGroupId: data.userGroupId,
    });
    onSaved();
  }

  return (
    <UserForm
      disabledMap={{ email: true }}
      defaultValues={{
        ...user,
        activateAccount: isActive,
        userGroupId: user.userGroup.id,
      }}
      onSave={saveUser}
    />
  );
}

// ─── Progress Section ─────────────────────────────────────────────────────────

function ProgressSection({ userId }: { userId: string }) {
  const { user, loading, refetch } = useUserById(userId);

  if (loading) return <LoadingRows rows={3} />;
  if (!user) return null;

  const userFullName =
    user.givenName.length > 0 || user.familyName.length > 0
      ? `${user.givenName} ${user.familyName}`
      : "No name";

  return (
    <div className={"space-y-6"}>
      <div className={"space-y-4"}>
        <h4
          className={"font-bold text-xs uppercase tracking-wide text-gray-500"}
        >
          Lesson feedback
        </h4>
        {user.lessonFeedbacks.length > 0 ? (
          <UserLessonFeedbackComponent
            onFeedbackDeleted={() => refetch()}
            user={user}
          />
        ) : (
          <InfoFeedback
            title={"No feedback"}
            message={`${userFullName} has not received any feedback since last progress check yet.`}
          />
        )}
      </div>
      <div className={"space-y-4"}>
        <h4
          className={"font-bold text-xs uppercase tracking-wide text-gray-500"}
        >
          Progress check results
        </h4>
        {user.progressCheckResults.length > 0 ? (
          <UserProgressCheckResultComponent user={user} />
        ) : (
          <InfoFeedback
            title={"No progress check"}
            message={`${userFullName} has not taken any progress check`}
          />
        )}
      </div>
    </div>
  );
}

// ─── Class Room Plans Section ─────────────────────────────────────────────────

function ClassRoomPlansSection({
  userId,
  onCreatePlan,
  onEditPlan,
}: {
  userId: string;
  onCreatePlan: () => void;
  onEditPlan: (userId: string, planId: string) => void;
}) {
  const { classRoomPlans, loading, refetch } = useUserClassRoomPlans(userId);
  const { remainingLessons, loading: loadingRemaining } =
    useUserRemainingLessons(userId);
  const { deleteClassRoomPlan, loading: deletingPlan } =
    useDeleteClassRoomPlan();
  const askConfirmation = useConfirm();

  async function onDelete(planId: string) {
    if (
      await askConfirmation({
        title: "Delete this plan?",
        confirmLabel: "Delete",
        isDestructive: true,
      })
    ) {
      await deleteClassRoomPlan(planId);
      await refetch();
    }
  }

  if (loading || loadingRemaining) return <LoadingRows rows={3} />;

  return (
    <div className={"space-y-4"}>
      <div className={"flex justify-between items-center"}>
        <p className={"text-sm text-gray-600"}>
          Remaining: <b>{remainingLessons}</b> hours
        </p>
        <PrimaryButton onClick={onCreatePlan} label={"New plan"} />
      </div>
      {classRoomPlans.length > 0 ? (
        <div className={"space-y-3"}>
          {classRoomPlans.map((p) => (
            <ClassRoomPlanListItem
              key={p.id}
              plan={p}
              deleting={deletingPlan}
              onDelete={onDelete}
              onEdit={(plan) => onEditPlan(plan.userId, plan.id)}
            />
          ))}
        </div>
      ) : (
        <InfoFeedback
          title={"No active plan"}
          message={"This user has no active plan."}
        />
      )}
      <div className={"border-t border-gray-200 pt-4"}>
        <UserFrozenWeekComponent userId={userId} />
      </div>
    </div>
  );
}

// ─── 1 on 1 Plans Section ─────────────────────────────────────────────────────

type PlanLocalState = {
  oneOnOneLessonType: OneOnOneLessonType | null;
  businessEnglishObjective: BusinessEnglishObjective | null;
  speakingLevel: SpeakingClassLevel | null;
};

function planToLocalState(
  plan: User1on1ClassroomPlanGraphqlDto,
): PlanLocalState {
  return {
    oneOnOneLessonType: plan.oneOnOneLessonType ?? null,
    businessEnglishObjective: plan.businessEnglishObjective ?? null,
    speakingLevel: plan.speakingLevel ?? null,
  };
}

function is1on1PlanDirty(
  local: PlanLocalState,
  plan: User1on1ClassroomPlanGraphqlDto,
): boolean {
  return (
    local.oneOnOneLessonType !== (plan.oneOnOneLessonType ?? null) ||
    local.businessEnglishObjective !==
      (plan.businessEnglishObjective ?? null) ||
    local.speakingLevel !== (plan.speakingLevel ?? null)
  );
}

const lessonTypeOptions: SelectOption<OneOnOneLessonType>[] = Object.values(
  OneOnOneLessonType,
).map((t) => ({ id: t, name: t }));

const businessEnglishObjectiveOptions: SelectOption<BusinessEnglishObjective>[] =
  Object.values(BusinessEnglishObjective).map((o) => ({ id: o, name: o }));

const speakingLevelOptions: SelectOption<SpeakingClassLevel>[] = Object.values(
  SpeakingClassLevel,
).map((l) => ({ id: l, name: l }));

function OneOnOnePlansSection({ userId }: { userId: string }) {
  const { plans, loading, refetch } = useUser1on1ClassroomPlans(userId);
  const { updateSpeakingLevel } = useUpdateUser1on1ClassroomPlanSpeakingLevel();
  const { updateLessonType } = useUpdateUser1on1ClassroomPlanLessonType();
  const [localStates, setLocalStates] = useState<
    Record<string, PlanLocalState>
  >({});
  const [savingPlanId, setSavingPlanId] = useState<string | null>(null);

  if (loading) return <LoadingRows rows={3} />;

  function getLocalState(
    plan: User1on1ClassroomPlanGraphqlDto,
  ): PlanLocalState {
    return localStates[plan.id] ?? planToLocalState(plan);
  }

  function setLocalState(
    plan: User1on1ClassroomPlanGraphqlDto,
    patch: Partial<PlanLocalState>,
  ) {
    setLocalStates((prev) => ({
      ...prev,
      [plan.id]: { ...(prev[plan.id] ?? planToLocalState(plan)), ...patch },
    }));
  }

  async function handleSave(plan: User1on1ClassroomPlanGraphqlDto) {
    const local = getLocalState(plan);
    setSavingPlanId(plan.id);
    try {
      if (
        local.speakingLevel !== (plan.speakingLevel ?? null) &&
        local.speakingLevel
      ) {
        await updateSpeakingLevel(plan.id, local.speakingLevel);
      }
      if (
        local.oneOnOneLessonType !== (plan.oneOnOneLessonType ?? null) ||
        local.businessEnglishObjective !==
          (plan.businessEnglishObjective ?? null)
      ) {
        if (local.oneOnOneLessonType) {
          const businessEnglishObjective =
            local.businessEnglishObjective ??
            BusinessEnglishObjective.Presentazioni;
          await updateLessonType(
            plan.id,
            local.oneOnOneLessonType,
            local.oneOnOneLessonType === OneOnOneLessonType.BusinessEnglish
              ? businessEnglishObjective
              : undefined,
          );
        }
      }
      await refetch();
      setLocalStates((prev) => {
        const next = { ...prev };
        delete next[plan.id];
        return next;
      });
    } finally {
      setSavingPlanId(null);
    }
  }

  if (plans.length === 0) {
    return (
      <InfoFeedback
        title={"No 1 on 1 plans"}
        message={"This user has no 1 on 1 plans."}
      />
    );
  }

  return (
    <div className={"space-y-3"}>
      {plans.map((plan: User1on1ClassroomPlanGraphqlDto) => {
        const local = getLocalState(plan);
        const isBusinessEnglish =
          local.oneOnOneLessonType === OneOnOneLessonType.BusinessEnglish;
        const dirty = is1on1PlanDirty(local, plan);
        const saving = savingPlanId === plan.id;
        return (
          <Card key={plan.id}>
            <div className={"p-4 space-y-2"}>
              <div className={"flex justify-between items-center"}>
                <h3 className={"text-base font-semibold"}>Plan {plan.id}</h3>
                <span className={"text-sm text-gray-500"}>
                  Created: {formatDate(plan.createdAt)}
                </span>
              </div>
              <div className={"grid grid-cols-2 gap-4 text-sm"}>
                <div>
                  <span className={"font-medium"}>Start Date:</span>{" "}
                  {formatDate(plan.startDateUtc)}
                </div>
                <div>
                  <span className={"font-medium"}>Plan Expiry:</span>{" "}
                  {formatDate(plan.endDateUtc)}
                </div>
                <div>
                  <span className={"font-medium"}>Last Lesson Date:</span>{" "}
                  {plan.lastLessonDateUtc
                    ? formatDate(plan.lastLessonDateUtc)
                    : "—"}
                </div>
                <div>
                  <span className={"font-medium"}>Lessons per Week:</span>{" "}
                  {plan.lessonsPerWeek}
                </div>
                <div>
                  <span className={"font-medium"}>Number of Weeks:</span>{" "}
                  {plan.numberOfWeeks}
                </div>
                <div className={"col-span-2"}>
                  <span className={"font-medium"}>Lesson Type:</span>
                  <div className={"mt-1"}>
                    <Select<OneOnOneLessonType>
                      disabled={saving}
                      value={local.oneOnOneLessonType ?? undefined}
                      options={lessonTypeOptions}
                      onChange={(lessonType) => {
                        if (!lessonType) return;
                        setLocalState(plan, {
                          oneOnOneLessonType: lessonType,
                          businessEnglishObjective:
                            lessonType === OneOnOneLessonType.BusinessEnglish
                              ? (local.businessEnglishObjective ??
                                BusinessEnglishObjective.Presentazioni)
                              : null,
                        });
                      }}
                    />
                  </div>
                </div>
                {isBusinessEnglish && (
                  <div className={"col-span-2"}>
                    <span className={"font-medium"}>
                      Business English Objective:
                    </span>
                    <div className={"mt-1"}>
                      <Select<BusinessEnglishObjective>
                        disabled={saving}
                        value={
                          local.businessEnglishObjective ??
                          BusinessEnglishObjective.Presentazioni
                        }
                        options={businessEnglishObjectiveOptions}
                        onChange={(objective) => {
                          if (!objective) return;
                          setLocalState(plan, {
                            businessEnglishObjective: objective,
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className={"col-span-2"}>
                  <span className={"font-medium"}>Speaking Level:</span>
                  <div className={"mt-1"}>
                    <Select<SpeakingClassLevel>
                      disabled={saving}
                      value={local.speakingLevel ?? undefined}
                      options={speakingLevelOptions}
                      onChange={(speakingLevel) => {
                        if (!speakingLevel) return;
                        setLocalState(plan, { speakingLevel });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={"flex justify-end pt-2"}>
                <PrimaryButton
                  label={saving ? "Saving..." : "Save"}
                  loading={saving}
                  disabled={!dirty || saving}
                  onClick={() => handleSave(plan)}
                />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Lessons Info Section ─────────────────────────────────────────────────────

function LessonsInfoSection({ userId }: { userId: string }) {
  const { lessonsInfo, loading, error } = useGetUserLessonsInfo(userId);

  if (loading) return <LoadingRows rows={3} />;
  if (error) return <ErrorFeedback title={"Error"} message={error.message} />;
  if (lessonsInfo.length === 0)
    return <p className={"text-gray-500"}>No lessons info available</p>;

  return <UserLessonsInfoTable lessonsInfo={lessonsInfo} />;
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export function UserDetailModal({
  user,
  onClose,
  sendResetPasswordEmail,
  sendingResetPasswordEmail,
  onNavigateToCreateClassRoomPlan,
  onNavigateToEditClassRoomPlan,
}: Props) {
  const navigate = useNavigate();
  const copyToClipboard = useCopyToClipboard();
  const [openSections, setOpenSections] = useState<Set<SectionId>>(new Set());

  function navigateTo(path: string) {
    onClose();
    navigate(path);
  }

  function toggleSection(section: SectionId) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }

  if (!user) return null;

  const userFullName =
    user.givenName.length > 0 || user.familyName.length > 0
      ? `${user.givenName} ${user.familyName}`
      : null;

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className={"relative z-10"} onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={"fixed inset-0 bg-black/25"} />
        </Transition.Child>

        <div className={"fixed inset-0 overflow-y-auto"}>
          <div
            className={
              "flex min-h-full md:items-center md:justify-center md:p-4 text-center"
            }
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={
                  "w-full max-w-5xl transform rounded-lg bg-white text-left shadow-xl transition-all"
                }
              >
                {/* Header */}
                <div
                  className={
                    "flex justify-between items-start p-6 border-b border-gray-200"
                  }
                >
                  <div>
                    <h2 className={"text-xl font-bold text-gray-900"}>
                      {user.email}
                    </h2>
                    {userFullName && (
                      <p className={"text-sm text-gray-500 mt-0.5"}>
                        {userFullName}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className={
                      "ml-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
                    }
                  >
                    <XMarkIcon className={"w-5 h-5 text-gray-500"} />
                  </button>
                </div>

                {/* Scrollable content */}
                <div className={"overflow-y-auto max-h-[80vh] p-6"}>
                  {/* Action buttons */}
                  <div
                    className={
                      "flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200"
                    }
                  >
                    {sendResetPasswordEmail && (
                      <button
                        onClick={() => sendResetPasswordEmail(user)}
                        disabled={sendingResetPasswordEmail}
                        className={
                          "inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium disabled:opacity-50"
                        }
                      >
                        <PaperAirplaneIcon className={"w-4 h-4"} />
                        {sendingResetPasswordEmail
                          ? "Sending..."
                          : "Send Credentials"}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        copyToClipboard(
                          `https://fluentepersempre.normasteaching.com/optin-referral-consulente?fps_referral_invited_by_id=${user.id}`,
                          "Referral link copied",
                        );
                      }}
                      className={
                        "inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium"
                      }
                    >
                      <ClipboardIcon className={"w-4 h-4"} />
                      Copy Closer Referral Link
                    </button>
                    <button
                      onClick={() =>
                        window.open(
                          `${import.meta.env.VITE_USER_APP_BASE_URL}/impersonate-user?uid=${user.authId}`,
                          "_blank",
                          "noopener noreferrer",
                        )
                      }
                      className={
                        "inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium"
                      }
                    >
                      <ArrowTopRightOnSquareIcon className={"w-4 h-4"} />
                      Impersonate
                    </button>
                  </div>

                  {/* Folder sections */}
                  <div className={"space-y-3"}>
                    <FolderSection
                      title={"Edit"}
                      isOpen={openSections.has("edit")}
                      onToggle={() => toggleSection("edit")}
                      onNavigateTo={() => navigateTo(`/users/${user.id}`)}
                    >
                      <EditSection userId={user.id} onSaved={onClose} />
                    </FolderSection>

                    <FolderSection
                      title={"User Progress"}
                      isOpen={openSections.has("progress")}
                      onToggle={() => toggleSection("progress")}
                      onNavigateTo={() =>
                        navigateTo(`/users/${user.id}/lesson-feedback-report`)
                      }
                    >
                      <ProgressSection userId={user.id} />
                    </FolderSection>

                    <FolderSection
                      title={"Class Room Plans"}
                      isOpen={openSections.has("classRoomPlans")}
                      onToggle={() => toggleSection("classRoomPlans")}
                      onNavigateTo={() =>
                        navigateTo(`/users/${user.id}/class-room-plans`)
                      }
                    >
                      <ClassRoomPlansSection
                        userId={user.id}
                        onCreatePlan={() =>
                          onNavigateToCreateClassRoomPlan(user.id)
                        }
                        onEditPlan={onNavigateToEditClassRoomPlan}
                      />
                    </FolderSection>

                    <FolderSection
                      title={"1 on 1 Classroom Plans"}
                      isOpen={openSections.has("oneOnOnePlans")}
                      onToggle={() => toggleSection("oneOnOnePlans")}
                      onNavigateTo={() =>
                        navigateTo(`/users/${user.id}/1on1-classroom-plans`)
                      }
                    >
                      <OneOnOnePlansSection userId={user.id} />
                    </FolderSection>

                    <FolderSection
                      title={"Lessons Info"}
                      isOpen={openSections.has("lessonsInfo")}
                      onToggle={() => toggleSection("lessonsInfo")}
                    >
                      <LessonsInfoSection userId={user.id} />
                    </FolderSection>

                    <FolderSection
                      title={"Classes"}
                      isOpen={openSections.has("classRoomHistory")}
                      onToggle={() => toggleSection("classRoomHistory")}
                      onNavigateTo={() =>
                        navigateTo(`/users/${user.id}/class-rooms`)
                      }
                    >
                      <UserClassRoomHistoryComponent
                        userId={user.id}
                        userFullName={userFullName ?? user.email}
                      />
                    </FolderSection>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
